import type { Color } from "@/composables/options/useColorOptions"
import type { Ref, InjectionKey } from "vue"
import { getLabelForRange } from "@/utils/dateFilterHelperFns"

export type GlobalReportQueryFilters = {
  start?: string
  end?: string
}

export const GLOBAL_REPORT_QUERY_FILTERS_KEY =
  Symbol() as InjectionKey<{
    readonly globalQuery: Ref<
      Readonly<GlobalReportQueryFilters>
    >
    readonly setGlobalQuery: (
      filters: GlobalReportQueryFilters
    ) => void
  }>

type UseReportQueryResult<
  TApiQuery extends GlobalReportQueryFilters,
> = {
  /**
   * Reactive list of local, per-series query filters.
   * Does not include the global date-range filters.
   * Each item also includes a `colorVariableName` used for chart/display assignment.
   */
  localQueries: Array<LocalQuery<TApiQuery>>

  /**
   * Computed full API queries, combining each local query with the shared global date filters.
   */
  apiQueries: ComputedRef<Array<TApiQuery>>

  /**
   * Adds another local query row, up to the internal maximum.
   */
  addLocalQuery: () => void

  /**
   * Removes the local query at the given index.
   * If only one remains, it is reset instead of removed.
   */
  removeLocalQuery: (index: number) => void

  /**
   * Shared date-range filters applied to every generated API query.
   */
  globalQuery: Ref<Readonly<GlobalReportQueryFilters>>

  /**
   * Replaces the shared global date-range filters.
   */
  setGlobalQuery: (
    filters: GlobalReportQueryFilters
  ) => void

  /**
   * Human-readable label for the current global date range.
   */
  timePeriodLabel: ComputedRef<string>
}

type LocalReportQueryFilters<
  TApiQuery extends GlobalReportQueryFilters,
> = Omit<TApiQuery, keyof GlobalReportQueryFilters>

export type LocalQuery<
  TApiQuery extends GlobalReportQueryFilters,
> = LocalReportQueryFilters<TApiQuery> & {
  colorVariableName: Color | undefined
}

export const useReportQuery = <
  TApiQuery extends GlobalReportQueryFilters,
>(
  defaultQueryFilters: Readonly<
    LocalReportQueryFilters<TApiQuery>
  >
): UseReportQueryResult<TApiQuery> => {
  const globalQueryObject = inject(
    GLOBAL_REPORT_QUERY_FILTERS_KEY
  )

  if (globalQueryObject === undefined) {
    throw new Error(
      "useReportQuery can only be used inside <ReportLayout />"
    )
  }

  const { globalQuery, setGlobalQuery } = globalQueryObject

  const availableColors = ref(
    useColorOptions().options.map(({ value }) => value)
  )

  const localQueries = reactive<
    Array<LocalQuery<TApiQuery>>
  >([
    {
      ...defaultQueryFilters,
      colorVariableName: availableColors.value.shift(),
    },
  ]) as Array<LocalQuery<TApiQuery>> // Needed to avoid complications from reactive return type deep unwrapping refs

  const addLocalQuery = () => {
    if (localQueries.length >= 10) return

    localQueries.push({
      ...defaultQueryFilters,
      colorVariableName: availableColors.value.shift(),
    })
  }

  const removeLocalQuery = (index: number) => {
    const color = localQueries[index].colorVariableName
    if (color) {
      availableColors.value.unshift(color)
    }

    if (localQueries.length === 1) {
      localQueries[0] = {
        ...defaultQueryFilters,
        colorVariableName: availableColors.value.shift(),
      }
    } else {
      localQueries.splice(index, 1)
    }
  }

  const timePeriodLabel = computed(() => {
    if (
      !globalQuery.value.start &&
      !globalQuery.value.end
    ) {
      return "All Time"
    } else if (
      !globalQuery.value.start &&
      globalQuery.value.end
    ) {
      return `Before ${DateFns.format(
        DateFns.parseISO(globalQuery.value.end),
        "do MMM yy"
      )}`
    } else if (
      globalQuery.value.start &&
      !globalQuery.value.end
    ) {
      return `After ${DateFns.format(
        DateFns.parseISO(globalQuery.value.start),
        "do MMM yy"
      )}`
    } else if (
      globalQuery.value.start &&
      globalQuery.value.end
    ) {
      return getLabelForRange(
        DateFns.parseISO(globalQuery.value.start),
        DateFns.parseISO(globalQuery.value.end)
      )
    }
    return ""
  })

  // @ts-expect-error There's edge cases (e.g. where TApiQuery contains colorVariableName) that cause an error
  const apiQueries = computed<Array<TApiQuery>>(() =>
    localQueries.map(
      ({ colorVariableName: _color, ...query }) => ({
        ...query,
        ...globalQuery.value,
      })
    )
  )

  return {
    localQueries,
    apiQueries,
    addLocalQuery,
    removeLocalQuery,
    globalQuery,
    setGlobalQuery,
    timePeriodLabel,
  }
}
